import { CardContentProps } from "../props/CardContentProps"
import React, { FC, PropsWithChildren, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { useSwipeable } from "react-swipeable"
import { useLongPress } from "use-long-press"
import { CardContentHeader } from "./content/CardContentHeader";
import { isNullOrEmpty } from "../../../helpers/ObjectHelpers";
import { isMobile } from "../../../helpers/DeviceHelpers";
import { useDarkmode } from "components/hooks/useAppStore";

export const CardContent: FC<PropsWithChildren<CardContentProps>> = ({
    ref,
    children,
    id,
    cardService,
    borderColor = "var(--card-border)",
    backgroundColor = "var(--card-body-bg)",
    indicatorPrimaryColor = "var(--card-border)",
    indicatorSecondaryColor = "",
    title,
    subTitle,
    subTitleClassName = "",
    icons,
    iconSize,
    actions,
    actionsDisplay,
    dispatcherService,
    onItemClick,
    isDirty = false,
    showDeleteButtonOnMobile = false,
    clipboardText = "",
    truncateSubTitle = true,
    onClick = null
}) => {

    const cancelClickListNames = [
        "k-button-text",
        "k-button-icon",
        "k-button-icontext",
        "k-button",
        "k-dateinput",
        "k-textbox",
        "k-dropdownlist",
        "k-disabled",
    ];

    const toggleContentClassName = (className: string) => {
        setContentClassName(className);
        setTimeout(() => { setContentClassName("") }, 2000);
    }

    useImperativeHandle(ref, () => ({
        onSuccess() {
            toggleContentClassName("crd-content-success");
        },
        onError() {
            toggleContentClassName("crd-content-error");
        }
    }));

    const [adjustedColor, setAdjustedColor] = useState(cardService.getColor(backgroundColor));
    const [adjustedIconColor, setAdjustedIconColor] = useState(cardService.getColor(borderColor));
    const [classNames, setClassNames] = useState<string[]>([]);
    const [borderWidth, setBorderWidth] = useState<number>(1);
    const [contentClassName, setContentClassName] = useState<string>("");

    const sectionRef = useRef<HTMLDivElement>(null);

    const darkmode = useDarkmode();

    const handleThemeChange = () => {
        setAdjustedColor(cardService.getColor(backgroundColor));
        setAdjustedIconColor(cardService.getColor(borderColor));
    }

    const setBaseValues = () => {
        const elem = sectionRef.current;
        if (!elem) return;
        cardService.setContentWidth(elem.getBoundingClientRect().width);
    }

    useEffect(() => {
        cardService.onContentClassNamesChange = setClassNames;
        cardService.onContentBorderWidthChange = setBorderWidth;

        window.addEventListener("resize", setBaseValues);

        return (() => {
            window.removeEventListener("resize", setBaseValues);

            if (sectionRef.current) {
                sectionRef.current = null; // Clear the reference
            }

            cardService.onContentClassNamesChange = () => { };
            cardService.onContentBorderWidthChange = () => { };
        })

    }, []);

    useEffect(() => {
        setBaseValues();
    }, [sectionRef]);

    useEffect(() => {
        handleThemeChange();
    }, [darkmode]);

    const onSwiped = () => {
        cardService.onSwiped();
    }
    const onSwipeStart = () => {
        cardService.onSwipeStart();
    }

    const onSwiping = (e: any) => {
        cardService.onSwiping(e);
    }

    const onTouchStartOrOnMouseDown = () => {
        if (!isMobile()) return;
        cardService.onTouchStartOrOnMouseDown();
    }

    // Helper function to check if event target is an interactive element that should prevent card click
    const shouldCancelClick = (e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
        const target = e.target as Element;
        if (!target) return false;

        // Check for specific CSS classes (original logic)
        if (target.classList && cancelClickListNames.some(className => target.classList.contains(className))) {
            return true;
        }

        // Check if target or any parent up to the card content is an interactive element
        let element: Element | null = target;
        while (element && !element.classList?.contains('crd-content')) {
            const tagName = element.tagName?.toLowerCase();

            // Allow clicks on form display labels and wrappers
            if (element.classList?.contains('k-form-label') ||
                element.classList?.contains('k-form-field-wrap') ||
                element.classList?.contains('k-d-grid')) {
                return false;
            }

            // Check for interactive HTML elements
            if (tagName === 'button' ||
                tagName === 'input' ||
                tagName === 'select' ||
                tagName === 'textarea' ||
                element.getAttribute('role') === 'button' ||
                element.hasAttribute('onclick') ||
                element.classList?.contains('btn') ||
                element.classList?.contains('button') ||
                // Check for elements that have click handlers or are focusable
                element.hasAttribute('tabindex') ||
                (element as HTMLElement).onclick !== null) {
                return true;
            }

            // Special case: links should only cancel if they're actual tel/mailto links
            // Read-only links (like in TextFieldWrapper for display) should not prevent card click
            if (tagName === 'a') {
                const href = element.getAttribute('href');
                if (href && (href.startsWith('http') || href.startsWith('https') || href.startsWith('/'))) {
                    return true;
                }
            }

            element = element.parentElement;
        }

        return false;
    };

    const onTouchEndOrOnMouseUp = async ({ event }: { event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement> }) => {
        if (!isMobile() || !event) return;

        if (shouldCancelClick(event)) return;

        dispatcherService.onClick(cardService.isLeadingActionsOpened, cardService.cancelOnTouchEndOrOnMouseUp);
    }

    const onCardClick = (e: React.MouseEvent<HTMLElement>) => {
        if (isMobile()) return;

        if (shouldCancelClick(e)) return;

        dispatcherService.onClick(cardService.isLeadingActionsOpened, cardService.cancelOnTouchEndOrOnMouseUp);
    }

    const onCardTouchEnd = (e: React.TouchEvent<HTMLElement>) => {
        if (!isMobile()) return;

        if (shouldCancelClick(e)) return;

        dispatcherService.onClick(cardService.isLeadingActionsOpened, cardService.cancelOnTouchEndOrOnMouseUp);
    }

    const onMouseDown = (e: React.MouseEvent<HTMLElement>) => {
        if (isMobile()) return;

        if (shouldCancelClick(e)) return;

        cardService.onTouchStartOrOnMouseDown();
    }

    const onMouseUp = (e: React.MouseEvent<HTMLElement>) => {
        if (isMobile()) return;

        if (shouldCancelClick(e)) return;

        dispatcherService.onClick(cardService.isLeadingActionsOpened, cardService.cancelOnTouchEndOrOnMouseUp);
    }

    const swipeHandlers = useSwipeable({
        trackMouse: true,
        onSwiped: onSwiped,
        onSwipeStart: onSwipeStart,
        onSwiping: onSwiping
    });

    const touchHandlers = useSwipeable({
        onTouchStartOrOnMouseDown, // Called for `touchstart` and `mousedown`
        onTouchEndOrOnMouseUp,     // Called for `touchend` and `mouseup`
    });

    const longPressCallback = useCallback((e: any) => {
        cardService.onLongPress();
    }, []);

    const longPressHandlers = useLongPress(longPressCallback, {
        filterEvents: event => true, // All events can potentially trigger long press (same as 'undefined')
        threshold: 500, // In milliseconds
        captureEvent: true, // Event won't get cleared after React finish processing it
        cancelOnMovement: 25, // Square side size (in pixels) inside which movement won't cancel long press
        cancelOutsideElement: true, // Cancel long press when moved mouse / pointer outside element while pressing
        detect: 'pointer', // Default option
    });

//    handleOnClick = {() => cardDispatch({ type: "CLICK" })}
//handleOnDelete = {(e: any | null) => cardDispatch({ type: "DELETE", payload: e })}

    return (
        <section
            ref={sectionRef}
            className={`crd ${cardService.classNameCrd} font-size-lg ${classNames.join(" ")}`}
            style={{ border: `solid ${borderWidth}px ${borderColor}` }}>
            <div
                style={getIndicatorStyle()}
                className="crd-indicator" />
            <div
                {...swipeHandlers}
                {...longPressHandlers()}
                onClick={onCardClick}
                onTouchEnd={onCardTouchEnd}
                onMouseDown={onMouseDown}
                style={{ backgroundColor: backgroundColor, color: adjustedColor, cursor: onClick ? 'pointer' : 'default' }}
                className={`crd-content ${contentClassName}`}>

                <CardContentHeader
                    id={id}
                    title={title}
                    subTitle={subTitle}
                    subTitleClassName={subTitleClassName}
                    icons={icons}
                    iconSize={iconSize}
                    isDirty={isDirty}
                    clipboardText={clipboardText}
                    borderColor={borderColor}
                    backgroundColor={backgroundColor}
                    actions={actions}
                    actionsDisplay={actionsDisplay}
                    adjustedColor={adjustedColor}
                    adjustedIconColor={adjustedIconColor}
                    cardService={cardService}
                    dispatcherService={dispatcherService}
                    showDeleteButtonOnMobile={showDeleteButtonOnMobile}
                    onItemClick={onItemClick}
                    truncateSubTitle={truncateSubTitle}
                />
                <main
                    className="crd-content-body">
                    {children}
                </main>
            </div>
        </section>
    )

    function getIndicatorStyle() {
        if (isNullOrEmpty(indicatorSecondaryColor)) {
            return { borderColor: borderColor, backgroundColor: indicatorPrimaryColor }
        } else {
            return {
                backgroundImage: `repeating-linear-gradient(45deg,${indicatorPrimaryColor},${indicatorPrimaryColor} 10px,${indicatorSecondaryColor} 0px,${indicatorSecondaryColor} 15px)`,
                border: 0
            }
        }
    }
}
